import { useEffect, useMemo } from 'react';
import { getImageUrl } from '@src/common/utils/image';
import { AdditionalType } from '@src/core/buyerApiClient';
import { useProductAdditional } from '@src/domains/products/hooks/useProductAdditional';
import { formatPriceLine } from '@src/domains/products/features/product-booking/utils/formatPriceLine';
import type { AdditionalRowProps } from '.';

export type UseAdditionalRowResult = {
  selected: boolean;

  imageUrl: string | undefined;
  title: string;
  priceLabel: string;
  availableAmount: number | null;
  statusText: string;
  showAvailability: boolean;
  showCheckbox: boolean;
  onToggle: () => void;

  descriptionText: string | undefined;

  showQuantityControls: boolean;
  quantity: number;
  quantityLabel: string;
  decrementAriaLabel: string;
  incrementAriaLabel: string;
  onDecrement: () => void;
  onIncrement: () => void;
  incrementDisabled: boolean;
  quantityError: string | undefined;

  showInput: boolean;
  input: string;
  inputPlaceholder: string | undefined;
  onInputChange: (value: string) => void;
};

export function useAdditionalRow(props: AdditionalRowProps): UseAdditionalRowResult {
  const { additional, widgetId, productId, i18n } = props;

  const { checked, input, quantity, availableAmount, isAvailable, quantityError, updateAdditional, trackToggle } =
    useProductAdditional({ widgetId, productId, additional, i18n });

  const statusText = useMemo(() => {
    if (isAvailable) {
      return `${i18n('adder_additionals_remain')}: ${availableAmount}`;
    }
    return i18n('adder_additionals_soldOff');
  }, [isAvailable, availableAmount, i18n]);

  const imageUrl = getImageUrl(additional.image, 'additional');
  const descriptionText = additional.description?.trim() || undefined;
  const priceLabel = formatPriceLine(additional, i18n);

  useEffect(() => {
    if (checked && !isAvailable) {
      updateAdditional({ checked: false, input: '', quantity: undefined });
    }
  }, [checked, isAvailable, updateAdditional]);

  const onToggle = () => {
    if (!isAvailable) {
      return;
    }

    const nextChecked = !checked;
    trackToggle(nextChecked);

    if (!nextChecked) {
      updateAdditional({ checked: false, input: '', quantity: undefined });
      return;
    }

    const initialQty = additional.type === AdditionalType.PerQuantity ? 1 : undefined;
    updateAdditional({ checked: true, input: '', quantity: initialQty });
  };

  const stepQuantity = (delta: number) => {
    const base = quantity ?? 1;
    const next = base + delta;
    const maxFromAdditional = additional.maxQuantity ?? Number.POSITIVE_INFINITY;
    const maxFromStock = availableAmount ?? Number.POSITIVE_INFINITY;
    const maxAllowed = Math.min(maxFromAdditional, maxFromStock);

    if (next < 1) {
      updateAdditional({ checked: false, quantity: undefined, input: '' });
      return;
    }

    if (next > maxAllowed) {
      return;
    }

    updateAdditional({ quantity: next });
  };

  const maxQuantityAllowed = Math.min(
    additional.maxQuantity ?? Number.POSITIVE_INFINITY,
    availableAmount ?? Number.POSITIVE_INFINITY,
  );
  const incrementDisabled = quantity !== undefined && quantity >= maxQuantityAllowed;

  const isPerQuantity = additional.type === AdditionalType.PerQuantity;

  return {
    selected: checked,

    imageUrl,
    title: additional.title,
    priceLabel,
    availableAmount,
    statusText,
    showAvailability: availableAmount !== null,
    showCheckbox: isAvailable,
    onToggle,

    descriptionText,

    showQuantityControls: isPerQuantity && checked,
    quantity: quantity ?? 1,
    quantityLabel: `${i18n('adder_quantity')}:`,
    decrementAriaLabel: i18n('product_booking_quantity_minus'),
    incrementAriaLabel: i18n('product_booking_quantity_plus'),
    onDecrement: () => stepQuantity(-1),
    onIncrement: () => stepQuantity(1),
    incrementDisabled,
    quantityError,

    showInput: Boolean(additional.enableInput) && checked,
    input,
    inputPlaceholder: additional.inputLabel ?? undefined,
    onInputChange: (value: string) => updateAdditional({ input: value }),
  };
}
